import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  IndianRupee, Download, FileText, Loader2, AlertTriangle, CheckCircle2,
  Wallet, RotateCcw, Clock, TrendingUp, Building2, CreditCard
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SOURCES = ['razorpay', 'offline', 'hudle', 'playo', 'district'];
const SOURCE_LABEL = { razorpay: 'Razorpay', offline: 'Offline / Cash', hudle: 'Hudle', playo: 'Playo', district: 'District' };
const SOURCE_COLOR = {
  razorpay: 'bg-blue-50 text-blue-700 border-blue-200',
  offline: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  hudle: 'bg-orange-50 text-orange-700 border-orange-200',
  playo: 'bg-purple-50 text-purple-700 border-purple-200',
  district: 'bg-rose-50 text-rose-700 border-rose-200',
};
const STREAMS = ['court', 'rentals', 'events', 'coaching', 'community'];

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

const download = async (path, filename) => {
  try {
    const res = await fetch(`${API}/api${path}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('Download ready', { description: filename });
  } catch (e) {
    toast.error('Download failed', { description: String(e.message || e) });
  }
};

const KPI = ({ icon: Icon, label, value, tone, testid }) => (
  <Card className={`p-4 border-l-4 ${tone}`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
      <Icon className="h-8 w-8 opacity-30" />
    </div>
  </Card>
);

export default function Finance() {
  const { user, isSuperAdmin } = useAuth();
  const [preset, setPreset] = useState('month');
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(todayStr());
  const [dateBasis, setDateBasis] = useState('collection');
  const [venueFilter, setVenueFilter] = useState('all');
  const [streamFilter, setStreamFilter] = useState('all');
  const [venues, setVenues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('summary');

  const qs = useMemo(() => {
    const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, date_basis: dateBasis });
    if (venueFilter !== 'all') p.set('facility_id', venueFilter);
    return p;
  }, [dateFrom, dateTo, dateBasis, venueFilter]);

  const applyPreset = (val) => {
    setPreset(val);
    if (val === 'today') { setDateFrom(todayStr()); setDateTo(todayStr()); }
    else if (val === 'week') { setDateFrom(addDays(-6)); setDateTo(todayStr()); }
    else if (val === 'month') { setDateFrom(monthStart()); setDateTo(todayStr()); }
  };

  const loadVenues = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/finance/venues`, { headers: authHeaders() });
      const data = await res.json();
      setVenues(data.venues || []);
    } catch { /* ignore */ }
  }, []);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams(qs);
      if (streamFilter !== 'all') p.set('stream', streamFilter);
      const res = await fetch(`${API}/api/finance/summary?${p.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSummary(await res.json());
    } catch (e) {
      toast.error('Failed to load finance summary', { description: String(e.message || e) });
    } finally { setLoading(false); }
  }, [qs, streamFilter]);

  useEffect(() => { loadVenues(); }, [loadVenues]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Manual-fallback venues (Razorpay settlement not auto-readable)
  const manualVenues = useMemo(() => {
    const scoped = venueFilter === 'all' ? venues : venues.filter(v => v.id === venueFilter);
    return scoped.filter(v => !v.settlement_readable);
  }, [venues, venueFilter]);

  const exportBase = useMemo(() => {
    const p = new URLSearchParams(qs);
    if (streamFilter !== 'all') p.set('stream', streamFilter);
    return p.toString();
  }, [qs, streamFilter]);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto" data-testid="finance-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-[#0f2747] flex items-center gap-2">
            <Wallet className="h-6 w-6" /> Finance &amp; Reconciliation
          </h1>
          <p className="text-sm text-gray-500">Reconcile money received per source, venue &amp; stream — read-only.</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex gap-1">
            {['today', 'week', 'month', 'custom'].map(pv => (
              <Button key={pv} size="sm" variant={preset === pv ? 'default' : 'outline'}
                onClick={() => applyPreset(pv)} data-testid={`finance-preset-${pv}`} className="capitalize">
                {pv}
              </Button>
            ))}
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPreset('custom'); }}
              className="h-9 w-40" data-testid="finance-date-from" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">To</label>
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPreset('custom'); }}
              className="h-9 w-40" data-testid="finance-date-to" />
          </div>
          {isSuperAdmin && (
            <div>
              <label className="text-xs text-gray-500 block mb-1">Venue</label>
              <Select value={venueFilter} onValueChange={setVenueFilter}>
                <SelectTrigger className="h-9 w-52" data-testid="finance-venue-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All venues</SelectItem>
                  {venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Stream</label>
            <Select value={streamFilter} onValueChange={setStreamFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="finance-stream-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All streams</SelectItem>
                {STREAMS.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Date basis</label>
            <Select value={dateBasis} onValueChange={setDateBasis}>
              <SelectTrigger className="h-9 w-44" data-testid="finance-date-basis"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="collection">Collection date</SelectItem>
                <SelectItem value="play">Booking / play date</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {loading && <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="summary" data-testid="finance-tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="finance-tab-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="outstanding" data-testid="finance-tab-outstanding">Outstanding</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="finance-tab-refunds">Refunds</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="finance-tab-settlements">Razorpay Recon</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-5">
          {summary && <SummaryView summary={summary} venues={venues} venueFilter={venueFilter}
            onCommissionSaved={loadSummary} exportBase={exportBase} />}
        </TabsContent>
        <TabsContent value="ledger"><LedgerView qs={qs.toString()} exportBase={exportBase} /></TabsContent>
        <TabsContent value="outstanding"><OutstandingView qs={qs.toString()} /></TabsContent>
        <TabsContent value="refunds"><RefundsView qs={qs.toString()} /></TabsContent>
        <TabsContent value="settlements"><SettlementsView qs={qs.toString()} manualVenues={manualVenues}
          venueFilter={venueFilter} dateFrom={dateFrom} dateTo={dateTo} streamFilter={streamFilter}
          isSuperAdmin={isSuperAdmin} ownFacilityId={user?.facility_id || user?.facilityId} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------- Summary ------------------------------- */
function SummaryView({ summary, venues, venueFilter, onCommissionSaved, exportBase }) {
  const recon = summary.reconciliation;
  const hasAgg = (summary.aggregator?.playo?.gross || 0) > 0 || (summary.aggregator?.hudle?.gross || 0) > 0 || (summary.aggregator?.district?.gross || 0) > 0;

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Wallet} label="Collected" value={inr(summary.collected)} tone="border-l-green-500" testid="finance-kpi-collected" />
        <KPI icon={RotateCcw} label="Refunded" value={inr(summary.refunded)} tone="border-l-orange-500" testid="finance-kpi-refunded" />
        <KPI icon={Clock} label="Outstanding" value={inr(summary.outstanding)} tone="border-l-amber-500" testid="finance-kpi-outstanding" />
        <KPI icon={TrendingUp} label="Net Retained" value={inr(summary.net_retained)} tone="border-l-[#0f2747]" testid="finance-kpi-net" />
      </div>

      {/* By source */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1"><CreditCard className="h-4 w-4" /> Collected by Payment Source</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SOURCES.map(s => (
            <Card key={s} className={`p-4 border ${SOURCE_COLOR[s]}`} data-testid={`finance-source-${s}`}>
              <p className="text-xs font-semibold uppercase">{SOURCE_LABEL[s]}</p>
              <p className="text-xl font-bold mt-1">{inr(summary.by_source[s])}</p>
              {summary.refunded_by_source[s] > 0 && (
                <p className="text-xs mt-1 opacity-80">− {inr(summary.refunded_by_source[s])} refunded</p>
              )}
              {(s === 'hudle' || s === 'playo' || s === 'district') && summary.by_source[s] > 0 && (
                <p className="text-[10px] mt-1 font-medium">gross · not net-to-bank</p>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* By venue */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1"><Building2 className="h-4 w-4" /> Collected by Venue</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="finance-venue-table">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-4">Venue</th>
              {SOURCES.map(s => <th key={s} className="py-2 px-3 text-right">{SOURCE_LABEL[s]}</th>)}
              <th className="py-2 pl-3 text-right">Total</th>
            </tr></thead>
            <tbody>
              {summary.by_venue.map(v => (
                <tr key={v.venue_id} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-medium">{v.venue_name}</td>
                  {SOURCES.map(s => <td key={s} className="py-2 px-3 text-right">{inr(v.by_source[s])}</td>)}
                  <td className="py-2 pl-3 text-right font-semibold">{inr(v.collected)}</td>
                </tr>
              ))}
              {summary.by_venue.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-gray-400">No collections in this period</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* By stream */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Collected by Revenue Stream</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {STREAMS.map(s => (
            <div key={s} className="bg-gray-50 rounded p-3" data-testid={`finance-stream-${s}`}>
              <p className="text-xs text-gray-500 capitalize">{s}</p>
              <p className="font-bold">{inr(summary.by_stream[s])}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Aggregator gross vs net + commission config */}
      {hasAgg && (
        <AggregatorCard summary={summary} venues={venues} venueFilter={venueFilter} onSaved={onCommissionSaved} />
      )}

      {/* Reconciliation status */}
      <Card className="p-4 border-l-4 border-l-emerald-500" data-testid="finance-recon-status">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
          <div>
            <p className="font-semibold text-gray-800">Reconciliation</p>
            <p className="text-sm text-gray-600">
              Collected <b>{inr(recon.collected)}</b> · Outstanding <b>{inr(recon.outstanding)}</b> ·
              Refunded <b>{inr(recon.refunded)}</b> · Net retained <b>{inr(recon.net_retained)}</b>.
            </p>
            <p className="text-xs text-gray-400 mt-1">Refunds and outstanding are tracked separately and never netted into collected.</p>
          </div>
        </div>
      </Card>

      {/* Exports */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1"><Download className="h-4 w-4" /> Download Reports (source-filterable)</h3>
        <div className="flex flex-wrap gap-2">
          {SOURCES.map(s => (
            <Button key={s} size="sm" variant="outline" data-testid={`finance-export-${s}`}
              onClick={() => download(`/finance/export?${exportBase}&source=${s}`, `Finance_${s}_${summary.period.date_from}_${summary.period.date_to}.csv`)}>
              <Download className="h-3.5 w-3.5 mr-1" /> {SOURCE_LABEL[s]} CSV
            </Button>
          ))}
          <Button size="sm" variant="outline" data-testid="finance-export-all"
            onClick={() => download(`/finance/export?${exportBase}`, `Finance_ALL_${summary.period.date_from}_${summary.period.date_to}.csv`)}>
            <Download className="h-3.5 w-3.5 mr-1" /> All Sources CSV
          </Button>
          <Button size="sm" className="bg-[#0f2747] hover:bg-[#1e3a5f]" data-testid="finance-export-pdf"
            onClick={() => download(`/finance/export-pdf?${exportBase}`, `Finance_Summary_${summary.period.date_from}_${summary.period.date_to}.pdf`)}>
            <FileText className="h-3.5 w-3.5 mr-1" /> PDF Summary
          </Button>
        </div>
      </Card>
    </>
  );
}

function AggregatorCard({ summary, venues, venueFilter, onSaved }) {
  const editable = venueFilter !== 'all';
  const venue = venues.find(v => v.id === venueFilter);
  const [playo, setPlayo] = useState(venue?.aggregator_commission?.playo ?? 0);
  const [hudle, setHudle] = useState(venue?.aggregator_commission?.hudle ?? 0);
  const [district, setDistrict] = useState(venue?.aggregator_commission?.district ?? 0);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPlayo(venue?.aggregator_commission?.playo ?? 0); setHudle(venue?.aggregator_commission?.hudle ?? 0); setDistrict(venue?.aggregator_commission?.district ?? 0); }, [venue]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/venue-commission`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ facility_id: venueFilter, playo: Number(playo), hudle: Number(hudle), district: Number(district) }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Commission saved');
      onSaved && onSaved();
    } catch (e) { toast.error('Save failed', { description: String(e.message || e) }); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-4" data-testid="finance-aggregator-card">
      <h3 className="text-sm font-semibold text-gray-700 mb-1">Aggregators — Gross vs Expected-Net</h3>
      <p className="text-xs text-gray-400 mb-3">Hudle/Playo collect from the customer and settle to you later, minus commission. Figures below are GROSS booking value — not net-to-bank.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {['playo', 'hudle', 'district'].map(a => {
          const d = summary.aggregator[a] || {};
          return (
            <div key={a} className="bg-gray-50 rounded p-3" data-testid={`finance-agg-${a}`}>
              <p className="font-semibold capitalize">{a}</p>
              <div className="text-sm mt-1 space-y-0.5">
                <p>Gross: <b>{inr(d.gross)}</b></p>
                <p>Commission (est): <b>{inr(d.commission_est)}</b></p>
                <p>Expected net: <b className="text-emerald-700">{inr(d.expected_net)}</b></p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 border-t pt-3">
        {editable ? (
          <div className="flex flex-wrap items-end gap-3">
            <div><label className="text-xs text-gray-500 block mb-1">Playo commission %</label>
              <Input type="number" value={playo} onChange={e => setPlayo(e.target.value)} className="h-9 w-28" data-testid="finance-comm-playo" /></div>
            <div><label className="text-xs text-gray-500 block mb-1">Hudle commission %</label>
              <Input type="number" value={hudle} onChange={e => setHudle(e.target.value)} className="h-9 w-28" data-testid="finance-comm-hudle" /></div>
            <div><label className="text-xs text-gray-500 block mb-1">District commission %</label>
              <Input type="number" value={district} onChange={e => setDistrict(e.target.value)} className="h-9 w-28" data-testid="finance-comm-district" /></div>
            <Button size="sm" onClick={save} disabled={saving} data-testid="finance-comm-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save commission'}
            </Button>
          </div>
        ) : (
          <p className="text-xs text-gray-400">Select a single venue to edit its Playo/Hudle commission %.</p>
        )}
      </div>
    </Card>
  );
}

/* ------------------------------- Ledger ------------------------------- */
function LedgerView({ qs, exportBase }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [src, setSrc] = useState('all');
  const [strm, setStrm] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams(qs);
      if (src !== 'all') p.set('source', src);
      if (strm !== 'all') p.set('stream', strm);
      const res = await fetch(`${API}/api/finance/ledger?${p.toString()}`, { headers: authHeaders() });
      const data = await res.json();
      setRows(data.transactions || []);
    } catch { toast.error('Failed to load ledger'); }
    finally { setLoading(false); }
  }, [qs, src, strm]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={src} onValueChange={setSrc}>
          <SelectTrigger className="h-9 w-44" data-testid="ledger-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {SOURCES.map(s => <SelectItem key={s} value={s}>{SOURCE_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={strm} onValueChange={setStrm}>
          <SelectTrigger className="h-9 w-40" data-testid="ledger-stream-filter"><SelectValue placeholder="Stream" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All streams</SelectItem>
            {STREAMS.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button size="sm" variant="outline" data-testid="ledger-export"
          onClick={() => download(`/finance/export?${exportBase}${src !== 'all' ? `&source=${src}` : ''}${strm !== 'all' ? `&stream=${strm}` : ''}`, `Finance_Ledger.csv`)}>
          <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
        </Button>
      </div>
      {loading ? <div className="py-8 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="ledger-table">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-2">Date</th><th className="py-2 px-2">Venue</th><th className="py-2 px-2">Ref</th>
              <th className="py-2 px-2">Customer</th><th className="py-2 px-2">Stream</th>
              <th className="py-2 px-2 text-right">Amount</th><th className="py-2 px-2">Source</th>
              <th className="py-2 px-2">Status</th><th className="py-2 px-2">Razorpay Payment ID</th>
            </tr></thead>
            <tbody>
              {rows.map((t, i) => (
                <tr key={i} className={`border-b last:border-0 ${t.kind === 'refund' ? 'bg-orange-50' : ''}`}>
                  <td className="py-1.5 pr-2 whitespace-nowrap">{t.collection_date}</td>
                  <td className="py-1.5 px-2">{t.venue_name}</td>
                  <td className="py-1.5 px-2 font-mono">{t.ref}</td>
                  <td className="py-1.5 px-2">{t.customer_name}</td>
                  <td className="py-1.5 px-2 capitalize">{t.stream}</td>
                  <td className={`py-1.5 px-2 text-right font-medium ${t.kind === 'refund' ? 'text-orange-700' : ''}`}>{t.kind === 'refund' ? '−' : ''}{inr(t.amount)}</td>
                  <td className="py-1.5 px-2"><Badge variant="outline" className="text-[10px]">{SOURCE_LABEL[t.source] || t.source}</Badge></td>
                  <td className="py-1.5 px-2">{t.status}</td>
                  <td className="py-1.5 px-2 font-mono text-[10px] break-all">{t.razorpay_payment_id || t.razorpay_refund_id || '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-gray-400">No transactions</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ---------------------------- Outstanding ---------------------------- */
function OutstandingView({ qs }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const res = await fetch(`${API}/api/finance/outstanding?${qs}`, { headers: authHeaders() }); setData(await res.json()); }
      catch { toast.error('Failed to load outstanding'); } finally { setLoading(false); }
    })();
  }, [qs]);
  return (
    <Card className="p-4">
      <p className="text-sm font-semibold mb-3">Outstanding (booked but not fully paid): <span className="text-amber-600">{inr(data.total)}</span></p>
      {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="outstanding-table">
          <thead><tr className="text-left text-gray-500 border-b">
            <th className="py-2 pr-3">Date</th><th className="py-2 px-3">Venue</th><th className="py-2 px-3">Ref</th>
            <th className="py-2 px-3">Customer</th><th className="py-2 px-3 text-right">Total</th>
            <th className="py-2 px-3 text-right">Collected</th><th className="py-2 px-3 text-right">Outstanding</th>
          </tr></thead>
          <tbody>{data.items.map((o, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="py-1.5 pr-3">{o.date}</td><td className="py-1.5 px-3">{o.venue_name}</td>
              <td className="py-1.5 px-3 font-mono text-xs">{o.ref}</td><td className="py-1.5 px-3">{o.customer_name}</td>
              <td className="py-1.5 px-3 text-right">{inr(o.total)}</td><td className="py-1.5 px-3 text-right">{inr(o.collected)}</td>
              <td className="py-1.5 px-3 text-right font-semibold text-amber-600">{inr(o.outstanding)}</td>
            </tr>))}
            {data.items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-gray-400">Nothing outstanding</td></tr>}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

/* ------------------------------- Refunds ------------------------------- */
function RefundsView({ qs }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const res = await fetch(`${API}/api/finance/refunds?${qs}`, { headers: authHeaders() }); setData(await res.json()); }
      catch { toast.error('Failed to load refunds'); } finally { setLoading(false); }
    })();
  }, [qs]);
  return (
    <Card className="p-4">
      <p className="text-sm font-semibold mb-3">Refunds issued: <span className="text-orange-600">{inr(data.total)}</span></p>
      {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="refunds-table">
          <thead><tr className="text-left text-gray-500 border-b">
            <th className="py-2 pr-3">Date</th><th className="py-2 px-3">Venue</th><th className="py-2 px-3">Ref</th>
            <th className="py-2 px-3">Customer</th><th className="py-2 px-3 text-right">Amount</th>
            <th className="py-2 px-3">Source</th><th className="py-2 px-3">Booking</th><th className="py-2 px-3">Razorpay Refund ID</th>
          </tr></thead>
          <tbody>{data.items.map((r, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="py-1.5 pr-3">{r.date}</td><td className="py-1.5 px-3">{r.venue_name}</td>
              <td className="py-1.5 px-3 font-mono text-xs">{r.ref}</td><td className="py-1.5 px-3">{r.customer_name}</td>
              <td className="py-1.5 px-3 text-right font-semibold text-orange-600">{inr(r.amount)}</td>
              <td className="py-1.5 px-3"><Badge variant="outline" className="text-[10px]">{SOURCE_LABEL[r.source] || r.source}</Badge></td>
              <td className="py-1.5 px-3">{r.booking_status}</td>
              <td className="py-1.5 px-3 font-mono text-[10px] break-all">{r.razorpay_refund_id || '—'}</td>
            </tr>))}
            {data.items.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-gray-400">No refunds in period</td></tr>}
          </tbody>
        </table></div>
      )}
    </Card>
  );
}

/* --------------------------- Razorpay Recon --------------------------- */
function SettlementsView({ qs, manualVenues, venueFilter, dateFrom, dateTo, streamFilter, isSuperAdmin, ownFacilityId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams(qs); p.set('source', 'razorpay'); p.set('include_refunds', 'false');
        if (streamFilter && streamFilter !== 'all') p.set('stream', streamFilter);
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 60000);
        const res = await fetch(`${API}/api/finance/ledger?${p.toString()}`, { headers: authHeaders(), signal: ctrl.signal });
        clearTimeout(timer);
        const data = await res.json(); setRows((data.transactions || []).filter(t => t.razorpay_payment_id));
      } catch (e) {
        if (e.name === 'AbortError') toast.error('Razorpay transactions timed out', { description: 'Try a shorter date range.' });
        else toast.error('Failed to load Razorpay transactions');
      } finally { setLoading(false); }
    })();
    setRecon(null);
  }, [qs, streamFilter]);

  const effectiveFacility = isSuperAdmin
    ? (venueFilter && venueFilter !== 'all' ? venueFilter : null)
    : (ownFacilityId || null);
  const singleVenue = !!effectiveFacility;

  const runRecon = async (force = false) => {
    if (!singleVenue) { toast.error('Select a single venue to auto-fetch settlements'); return; }
    setReconLoading(true);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 100000);
    try {
      const p = new URLSearchParams({ facility_id: effectiveFacility, date_from: dateFrom, date_to: dateTo, force_refresh: force });
      const res = await fetch(`${API}/api/finance/razorpay-recon?${p.toString()}`, { headers: authHeaders(), signal: ctrl.signal });
      const data = await res.json();
      if (data.error) {
        const desc = data.error === 'range_too_large'
          ? (data.message || 'Range too large — try a shorter date range.')
          : (data.message || data.error);
        toast.error('Recon failed', { description: desc });
        setRecon(data);
      } else {
        setRecon(data);
        if (data.partial) toast.warning('Partial results', { description: data.partial_message || 'Some settlement days could not be fetched — try a shorter range.' });
      }
    } catch (e) {
      if (e.name === 'AbortError') toast.error('Recon timed out', { description: 'This range took too long. Try a shorter date range.' });
      else toast.error('Recon failed', { description: String(e.message || e) });
    } finally { clearTimeout(timer); setReconLoading(false); }
  };

  const repairBacklog = async () => {
    setRepairing(true);
    try {
      const p = new URLSearchParams(); if (effectiveFacility) p.set('facility_id', effectiveFacility);
      const res = await fetch(`${API}/api/finance/repair-razorpay-refs?${p.toString()}`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      toast.success('Backlog repair complete', { description: `Repaired ${data.repaired} bookings · ${data.allocations_repaired ?? 0} allocations · skipped ${data.skipped ?? 0} · scanned ${data.scanned} (of ${data.candidates ?? data.scanned} online bookings)` });
    } catch (e) { toast.error('Repair failed', { description: String(e.message || e) }); }
    finally { setRepairing(false); }
  };

  const backfillFromRecon = async () => {
    if (!singleVenue) { toast.error('Select a single venue first'); return; }
    setBackfilling(true);
    try {
      const p = new URLSearchParams({ facility_id: effectiveFacility, date_from: dateFrom, date_to: dateTo });
      const res = await fetch(`${API}/api/finance/backfill-from-recon?${p.toString()}`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.error) { toast.error('Backfill failed', { description: data.message || data.error }); }
      else {
        const parts = [
          `wrote ${data.written || 0} id(s)`,
          `appended ${data.appended || 0} new allocation(s)`,
          `${data.already_linked || 0} already linked`,
          `skipped ${data.skipped || 0}`,
          (data.skipped_overpayment || 0) > 0 ? `overpayment-skipped ${data.skipped_overpayment}` : null,
          `failed ${data.failed || 0}`,
        ].filter(Boolean).join(' · ');
        toast.success('Backfill complete', {
          description: `${parts} (of ${data.candidates || 0} QR matches)`
        });
        runRecon(true);
      }
    } catch (e) { toast.error('Backfill failed', { description: String(e.message || e) }); }
    finally { setBackfilling(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 border-l-4 border-l-blue-500">
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <p className="text-sm text-gray-700 flex-1 min-w-[280px]"><b>Phase 2 — Auto-reconciliation.</b> Fetch this venue's actual Razorpay settlements and auto-match them against PlaySpaces records. Mismatches are flagged below.</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={repairBacklog} disabled={repairing} data-testid="finance-repair-refs">
              {repairing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />} Repair missing references
            </Button>
            <Button size="sm" className="bg-[#0f2747] hover:bg-[#1e3a5f]" onClick={() => runRecon(false)} disabled={reconLoading || !singleVenue} data-testid="finance-run-recon">
              {reconLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CreditCard className="h-3.5 w-3.5 mr-1" />} Fetch settlements & auto-match
            </Button>
          </div>
        </div>
        {!singleVenue && isSuperAdmin && <p className="text-xs text-amber-600 mt-2">Select a single venue (top filter) to auto-fetch its Razorpay settlements.</p>}
      </Card>

      {manualVenues.length > 0 && (
        <Card className="p-4 border-l-4 border-l-amber-500 bg-amber-50" data-testid="finance-manual-fallback">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <div>
              <p className="font-semibold text-amber-800 text-sm">Manual matching — settlement data not available</p>
              <p className="text-xs text-amber-700 mt-0.5">These venues' Razorpay keys can't read settlement data (no live key/secret), so auto-recon can't cover them yet:</p>
              <p className="text-sm text-amber-900 font-medium mt-1">{manualVenues.map(v => v.name).join(', ')}</p>
            </div>
          </div>
        </Card>
      )}

      {recon && recon.settlement_readable && !recon.error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI icon={Wallet} label="Recorded (PlaySpaces)" value={inr(recon.totals.playspaces_recorded)} tone="border-l-blue-500" testid="recon-kpi-recorded" />
            <KPI icon={CreditCard} label="Settled (Razorpay)" value={inr(recon.totals.razorpay_settled)} tone="border-l-indigo-500" testid="recon-kpi-settled" />
            <KPI icon={RotateCcw} label="Fees + Tax" value={inr(recon.totals.fees)} tone="border-l-orange-500" testid="recon-kpi-fees" />
            <KPI icon={TrendingUp} label="Net to Bank" value={inr(recon.totals.net_to_bank)} tone="border-l-emerald-600" testid="recon-kpi-net" />
          </div>
          <Card className={`p-4 border-l-4 ${recon.reconciled ? 'border-l-emerald-500' : 'border-l-red-500'}`} data-testid="recon-status">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                {recon.reconciled ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-red-600" />}
                <p className="text-sm font-semibold">{recon.reconciled ? 'Fully reconciled — every settled payment matches a PlaySpaces record.' : 'Discrepancies found — review flags below.'}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" data-testid="recon-download-csv"
                  onClick={() => download(`/finance/razorpay-recon-export?facility_id=${effectiveFacility}&date_from=${dateFrom}&date_to=${dateTo}`, `Recon_${effectiveFacility}_${dateFrom}_to_${dateTo}.csv`)}>
                  <Download className="h-3.5 w-3.5 mr-1" /> Download recon CSV
                </Button>
                {(recon.counts.matched_by_booking || 0) > 0 && (
                  <Button size="sm" variant="outline" onClick={backfillFromRecon} disabled={backfilling}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="recon-backfill-from-recon">
                    {backfilling ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
                    Backfill {recon.counts.matched_by_booking} payment(s) to bookings
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Matched {recon.counts.matched} · Amount mismatches {recon.counts.amount_mismatch} · Matched by booking (QR) {recon.counts.matched_by_booking || 0}{(recon.counts.matched_cross_period || 0) > 0 ? ` · Matched cross-period ${recon.counts.matched_cross_period}` : ''} · Only in PlaySpaces {recon.counts.only_in_playspaces} · Only in Razorpay {recon.counts.only_in_razorpay} · {recon.cached ? 'cached' : 'live'} fetch</p>
          </Card>

          {recon.partial && (
            <Card className="p-3 border-l-4 border-l-amber-500 bg-amber-50" data-testid="recon-partial-banner">
              <p className="text-xs text-amber-800 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0" /> {recon.partial_message || 'Some settlement days could not be fetched — results may be incomplete. Try a shorter date range or refresh.'}</p>
            </Card>
          )}

          {(recon.counts.matched_by_booking || 0) > 0 && (
            <ReconTable title="✓ Matched via booking notes (QR) — settled in Razorpay, linked to a booking" testid="recon-matched-booking" rows={recon.matched_by_booking}
              cols={[['booking_id', 'Booking'], ['booking_date', 'Booking Date'], ['court_name', 'Court'], ['booking_type', 'Type'], ['customer_name', 'Customer'], ['payment_method', 'Method'], ['entity_id', 'Payment ID'], ['amount', 'Razorpay ₹', true], ['playspaces_amount', 'PlaySpaces ₹', true], ['settlement_id', 'Settlement']]} tone="text-emerald-700" />
          )}

          {(recon.counts.matched_cross_period || 0) > 0 && (
            <ReconTable title={`✓ Matched cross-period — ${recon.counts.matched_cross_period} payment(s) settled in this window but recorded in PlaySpaces outside it (no action needed)`} testid="recon-matched-cross-period" rows={recon.matched_cross_period}
              cols={[['booking_id', 'Booking'], ['booking_date', 'Booking Date'], ['court_name', 'Court'], ['booking_type', 'Type'], ['customer_name', 'Customer'], ['payment_method', 'Method'], ['entity_id', 'Payment ID'], ['amount', 'Razorpay ₹', true], ['settlement_id', 'Settlement']]} tone="text-gray-600" />
          )}

          {recon.counts.amount_mismatch > 0 && (
            <ReconTable title="⚠ Amount mismatches" testid="recon-mismatch" rows={recon.amount_mismatch}
              cols={[['booking_id', 'Booking'], ['booking_date', 'Booking Date'], ['venue_name', 'Venue'], ['court_name', 'Court'], ['booking_type', 'Type'], ['customer_name', 'Customer'], ['booked_by', 'Booked By'], ['payment_id', 'Payment ID'], ['amount', 'PlaySpaces ₹', true], ['razorpay_amount', 'Razorpay ₹', true], ['net_settled', 'Net ₹', true]]} tone="text-red-700" />
          )}
          {recon.counts.only_in_playspaces > 0 && (
            <ReconTable title="⚠ Recorded in PlaySpaces but NOT in Razorpay settlement (unsettled / missing)" testid="recon-only-ps" rows={recon.only_in_playspaces}
              cols={[['booking_id', 'Booking'], ['booking_date', 'Booking Date'], ['venue_name', 'Venue'], ['court_name', 'Court'], ['booking_type', 'Type'], ['customer_name', 'Customer'], ['payment_method', 'Method'], ['payment_id', 'Payment ID'], ['amount', 'Amount ₹', true]]} tone="text-amber-700" />
          )}
          {recon.counts.only_in_razorpay > 0 && (
            <ReconTable title="⚠ Settled in Razorpay but NO PlaySpaces record" testid="recon-only-rz" rows={recon.only_in_razorpay}
              cols={[['entity_id', 'Payment ID'], ['payment_date', 'Payment Date'], ['customer_name', 'Customer'], ['customer_contact', 'Contact'], ['customer_email', 'Email'], ['payment_method', 'Method'], ['description', 'Description'], ['notes', 'Notes'], ['amount', 'Razorpay ₹', true], ['fee', 'Fee ₹', true], ['settlement_id', 'Settlement']]} tone="text-red-700" />
          )}
        </>
      )}

      <Card className="p-4">
        <p className="text-sm font-semibold mb-3">PlaySpaces-recorded Razorpay transactions</p>
        {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6" /> : (
          <div className="overflow-x-auto"><table className="w-full text-xs" data-testid="settlements-table">
            <thead><tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-2">Date</th><th className="py-2 px-2">Venue</th><th className="py-2 px-2">Ref</th>
              <th className="py-2 px-2">Customer</th><th className="py-2 px-2 text-right">Amount</th>
              <th className="py-2 px-2">Payment ID</th><th className="py-2 px-2">Order ID</th><th className="py-2 px-2">UPI RRN</th>
            </tr></thead>
            <tbody>{rows.map((t, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1.5 pr-2">{t.collection_date}</td><td className="py-1.5 px-2">{t.venue_name}</td>
                <td className="py-1.5 px-2 font-mono">{t.ref}</td><td className="py-1.5 px-2">{t.customer_name}</td>
                <td className="py-1.5 px-2 text-right font-medium">{inr(t.amount)}</td>
                <td className="py-1.5 px-2 font-mono text-[10px] break-all">{t.razorpay_payment_id || '—'}</td>
                <td className="py-1.5 px-2 font-mono text-[10px] break-all">{t.razorpay_order_id || '—'}</td>
                <td className="py-1.5 px-2 font-mono text-[10px] break-all">{t.razorpay_bank_rrn || '—'}</td>
              </tr>))}
              {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-gray-400">No Razorpay transactions in period</td></tr>}
            </tbody>
          </table></div>
        )}
      </Card>
    </div>
  );
}

function ReconTable({ title, rows, cols, tone, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <p className={`text-sm font-semibold mb-2 ${tone}`}>{title} ({rows.length})</p>
      <div className="overflow-x-auto"><table className="w-full text-xs">
        <thead><tr className="text-left text-gray-500 border-b">
          {cols.map(([k, label, num]) => <th key={k} className={`py-2 px-2 ${num ? 'text-right' : ''}`}>{label}</th>)}
        </tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-b last:border-0">
            {cols.map(([k, label, num]) => <td key={k} className={`py-1.5 px-2 ${num ? 'text-right font-medium' : 'font-mono text-[10px] break-all'}`}>{num ? inr(r[k]) : (r[k] || '—')}</td>)}
          </tr>))}
        </tbody>
      </table></div>
    </Card>
  );
}
